/**
 * @GestionalePD Reset Password UI
 */
import React, { useEffect, useState } from 'react';
import {
  Grid,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { Navigate, useLocation, Link } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
//------------------------------------------------------
import { POPUP_DURATION_SUCCESS, usePopup } from '../context/PopupContext';
import { StyledRegistrationCard } from '../utils/StyledElements';
import { DefaultContainedButton } from '../utils/Buttons';
import { CustomFormControl } from '../utils/StyledInputs';
import { LoadingUI } from '../utils/LayoutStyles';
import { HttpErrorHandler } from '../utils/ErrorHandler';
import API from '../api';

/**
 * @returns Reset Password Page UI
 */
export default function ResetPasswordPage() {
  const is600Screen = useMediaQuery('(min-width: 600px)');

  // Get URL Link
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const resetID = params.get('id');

  const [validResetLink, setValidResetLink] = useState(false);
  const [isCheckingLink, setIsCheckingLink] = useState(true);

  // Check if reset link is valid
  useEffect(() => {
    if (resetID) {
      API.get(`/checkpwdlink/${resetID}`)
        .then((response) => {
          setIsCheckingLink(false);
          setValidResetLink(true);
        })
        .catch((error) => {
          setIsCheckingLink(false);
          setValidResetLink(false);
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    } else {
      setValidResetLink(false);
      setIsCheckingLink(false);
    }
  }, [resetID]);

  // Visualization of input field values
  const [showNewPassword, setShowNewPassword] = useState({});
  const [showRepeatPassword, setShowRepeatPassword] = useState({});

  // visualization of input value handler function
  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  const handleClickShowRepeatPassword = () => setShowRepeatPassword((show) => !show);

  // set redirect value
  const [redirect, setRedirect] = useState(false);

  // Popup
  const { showPopup, setPopUpText } = usePopup();

  // Reset password request
  const onResetPassword = async (event) => {
    try {
      event.preventDefault();

      const data = new FormData(event.currentTarget);
      const repeatNewPassword = data.get('repeatPassword');
      const newPassword = data.get('newPassword');

      if (newPassword !== repeatNewPassword) {
        throw new Error('Le password inserite devono essere uguali');
      }

      const isMaiuscValid = /^(?=.*[A-Z])/.test(newPassword);
      const isNumberValid = /(?=.*\d)/.test(newPassword);
      const isSpecialValid = /(?=.*[!?@#$%^&*()_+-])/.test(newPassword);

      if (!isMaiuscValid) {
        throw new Error('La password deve contenere almeno una lettera maiuscola');
      }

      if (!isNumberValid) {
        throw new Error('La password deve contenere almeno un numero');
      }

      if (!isSpecialValid) {
        throw new Error('La password deve contenere almeno un carattere speciale');
      }

      if (newPassword.length < 8) {
        throw new Error('La password deve contenere almeno 8 caratteri');
      }

      const resetPassword = {
        new_password: newPassword,
        link_reset: resetID,
      };

      await API.patch(`/users/resetlink_changepwd`, resetPassword);
      showPopup(true, 'Password modificata con successo');
      setTimeout(() => {
        setRedirect(true);
      }, POPUP_DURATION_SUCCESS + 400);
    } catch (error) {
      const errorMessage = error.message;
      showPopup(false, errorMessage);
    }
  };

  // redirect to login
  if (redirect) {
    return <Navigate to={{ pathname: '/' }} />;
  }

  return (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="center"
      sx={{ width: '100vw', height: '100vh' }}
      className="default-bg"
    >
      {isCheckingLink ? (
        <StyledRegistrationCard is600Screen={is600Screen}>
          {LoadingUI()}
          <Box />
        </StyledRegistrationCard>
      ) : validResetLink ? (
        <StyledRegistrationCard is600Screen={is600Screen}>
          {/* Logo */}
          <Box width="125px">
            <Box component="img" width="100%" src="assets/images/img/logopd.png" alt="" />
          </Box>

          <Typography variant="h3" sx={{ marginTop: '15px' }}>
            Reset Password
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
            <Typography sx={{ textAlign: 'start' }}>La password deve contenere almeno:</Typography>
            <Typography sx={{ textAlign: 'start' }}>&#x2022; 8 caratteri</Typography>
            <Typography sx={{ textAlign: 'start' }}>&#x2022; Una lettera maiuscola</Typography>
            <Typography sx={{ textAlign: 'start' }}>&#x2022; Un numero</Typography>
            <Typography sx={{ textAlign: 'start' }}>
              &#x2022; Uno dei seguenti caratteri speciali !?-@#$%^&*()_+
            </Typography>
          </Box>

          <form method="post" onSubmit={onResetPassword}>
            <Grid container justifyContent="center" alignItems="center" spacing={2}>
              {/* New Password */}
              <Grid item xs={11} md={9}>
                <CustomFormControl>
                  <InputLabel htmlFor="outlined-new-password">Nuova Password</InputLabel>
                  <OutlinedInput
                    id="outlined-new-password"
                    type={showNewPassword ? 'password' : 'text'}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowNewPassword}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseUp={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Nuova Password"
                    name="newPassword"
                  />
                </CustomFormControl>
              </Grid>

              {/* Repeat Password */}
              <Grid item xs={11} md={9}>
                <CustomFormControl>
                  <InputLabel htmlFor="outlined-repeat-password">Ripeti Nuova Password</InputLabel>
                  <OutlinedInput
                    id="outlined-repeat-password"
                    type={showRepeatPassword ? 'password' : 'text'}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowRepeatPassword}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseUp={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Ripeti Nuova Password"
                    name="repeatPassword"
                  />
                </CustomFormControl>
              </Grid>

              {/* Submit Button  */}
              <Grid item xs={11} md={9}>
                <DefaultContainedButton
                  fullWidth
                  customStyles={{
                    marginTop: '10px',
                    height: '40px',
                    width: '100%',
                  }}
                  type="submit"
                >
                  Invia
                </DefaultContainedButton>

              </Grid>
            </Grid>
          </form>
        </StyledRegistrationCard>
      ) : (
        <StyledRegistrationCard is600Screen={is600Screen}>
          <Stack direction="column" alignItems="center" justifyContent="start" gap="24px" sx={{ padding: '24px' }}>
            <ErrorOutlineIcon sx={{ fontSize: '5em', color: 'red' }} />
            <Typography variant="h5">
              Il link per il reset della password è scaduto, riesegui la procedura di recupero per ricevere un altro
              link{' '}
            </Typography>
            {/* Redirect To login page  */}
            <Link style={{ textDecoration: 'none', paddingTop: 4 }} to={'/'}>
              <ArrowBackIosNew sx={{ fontSize: '.8em', paddingTop: '2px' }} /> Torna alla pagina login
            </Link>
          </Stack>
          <Box />
        </StyledRegistrationCard>
      )}
    </Stack>
  );
}
